import { db } from '$db/local/db';
import type { SleepRecord, UUID } from '$domain/types';
import { getActiveUserId } from '$core/identity/active-user';
import { newId, nowIso, notDeleted } from './common';

export interface UpsertSleepInput {
  date: string;
  bedtime: string;
  wakeTime: string;
  durationMinutes: number;
  quality: number;
}

export async function listSleepRecords(
  userId = getActiveUserId()
): Promise<SleepRecord[]> {
  const rows = await db.sleepRecords.where('userId').equals(userId).toArray();
  return rows
    .filter(notDeleted)
    .sort((a, b) => b.date.localeCompare(a.date));
}

export async function getSleepRecordByDate(
  date: string,
  userId = getActiveUserId()
): Promise<SleepRecord | undefined> {
  const rows = await db.sleepRecords.where('userId').equals(userId).toArray();
  return rows.find((r) => r.date === date && notDeleted(r));
}

export async function upsertSleepRecord(
  input: UpsertSleepInput
): Promise<SleepRecord> {
  const existing = await getSleepRecordByDate(input.date);
  const now = nowIso();
  if (existing) {
    await db.sleepRecords.update(existing.id, {
      bedtime: input.bedtime,
      wakeTime: input.wakeTime,
      durationMinutes: input.durationMinutes,
      quality: input.quality,
      updatedAt: now
    });
    const updated = await db.sleepRecords.get(existing.id);
    return updated!;
  }
  const record: SleepRecord = {
    id: newId(),
    userId: getActiveUserId(),
    date: input.date,
    bedtime: input.bedtime,
    wakeTime: input.wakeTime,
    durationMinutes: input.durationMinutes,
    quality: input.quality,
    createdAt: now,
    updatedAt: now,
    deletedAt: null
  };
  await db.sleepRecords.add(record);
  return record;
}

export async function deleteSleepRecord(id: UUID): Promise<void> {
  await db.sleepRecords.update(id, { deletedAt: nowIso(), updatedAt: nowIso() });
}